import type { ComponentProps, ReactNode } from 'react';
import { useCallback } from 'react';

import type { ApiFilestackImageData } from '#/api/types/ApiFilestackImageData';
import { ImagePicker } from '#/components/ImagePicker/ImagePicker';
import { useI18n } from '#/i18n/hooks/useI18n';
import { showModal } from '#/zustand-stores/modalStore';

import { Field } from './Field/Field';

type Props = {
  altText?: string;
  aspectRatio?: number;
  defaultImage?: ReactNode;
  hideRemove?: boolean;
  id?: string;
  name: string;
  label?: string;
  description?: string;
  previewCrop?: boolean;
  previewCropToCircle?: boolean;
  previewWidth?: number;
  previewHeight?: number;
  format?: 'logo' | 'banner' | 'avatar';
  message?: string;
  centerButton?: boolean;
  overlayButton?: boolean;
  disableCrop?: boolean;
  qaId?: string;
  confirmModal?: boolean;
  onChange?: (newImage: ApiFilestackImageData | null) => void;
  noMargin?: boolean;
  inlineLabel?: boolean;
  recommendedDimensions?: {
    width: number;
    height: number;
  };
  extraAnalyticsData?: Record<string, unknown>;
  allowSVG?: boolean;
  image?: ApiFilestackImageData | null;
  saving?: boolean;
  showCropScreen?: boolean;
};

export function ImagePickerField({
  altText,
  aspectRatio,
  defaultImage,
  name,
  label,
  description,
  previewWidth,
  previewHeight,
  format,
  message,
  centerButton,
  overlayButton,
  disableCrop,
  confirmModal,
  onChange,
  qaId,
  recommendedDimensions,
  inlineLabel,
  noMargin,
  extraAnalyticsData,
  allowSVG,
  previewCropToCircle,
  previewCrop,
  id,
  hideRemove,
  image,
  saving,
  showCropScreen,
}: Props) {
  const { i18n } = useI18n();

  const Picker = useCallback(
    (
      pickerProps: Pick<
        ComponentProps<typeof ImagePicker>,
        'qaId' | 'image' | 'showConfirmationModal' | 'onChange'
      >,
    ) => (
      <ImagePicker
        altText={altText}
        defaultImage={defaultImage}
        extraAnalyticsData={extraAnalyticsData}
        name={name}
        previewWidth={previewWidth}
        previewHeight={previewHeight}
        format={format}
        message={message}
        centerButton={centerButton}
        overlayButton={overlayButton}
        disableCrop={disableCrop}
        allowSVG={allowSVG}
        aspectRatio={aspectRatio}
        previewCropToCircle={previewCropToCircle}
        previewCrop={previewCrop}
        id={id}
        hideRemove={hideRemove}
        recommendedDimensions={recommendedDimensions}
        label={label}
        qaId={pickerProps.qaId}
        image={pickerProps.image}
        showConfirmationModal={pickerProps.showConfirmationModal}
        onChange={pickerProps.onChange}
        saving={saving}
        showCropScreen={showCropScreen}
      />
    ),
    [
      altText,
      defaultImage,
      extraAnalyticsData,
      name,
      previewWidth,
      previewHeight,
      format,
      message,
      centerButton,
      overlayButton,
      disableCrop,
      allowSVG,
      aspectRatio,
      previewCropToCircle,
      previewCrop,
      id,
      hideRemove,
      recommendedDimensions,
      label,
      saving,
      showCropScreen,
    ],
  );

  return (
    <Field
      name={name}
      label={label}
      inlineLabel={inlineLabel}
      noMargin={noMargin}
      description={description}
      renderInput={({ value, qaId: autoQaId, updateValue }) =>
        confirmModal ? (
          <Picker
            showConfirmationModal={() => {
              showModal('CONFIRMATION', {
                qaId: 'confirm-remove-logo',
                type: 'danger',
                title: i18n('Remove Image'),
                buttonText: i18n('Remove'),
                body: i18n('Are you sure you want to remove this image?'),
                onConfirm: () => {
                  if (onChange) onChange(null);
                  else updateValue(null);
                  return Promise.resolve();
                },
              });
            }}
            // TODO: Fix this the next time the file is edited.
            // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
            image={typeof image !== 'undefined' ? image : value}
            qaId={qaId ?? autoQaId}
            onChange={onChange ?? updateValue}
          />
        ) : (
          <Picker
            // TODO: Fix this the next time the file is edited.
            // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
            image={typeof image !== 'undefined' ? image : value}
            qaId={qaId ?? autoQaId}
            onChange={(e) => {
              if (onChange) onChange(e);
              else updateValue(e);
            }}
          />
        )
      }
    />
  );
}
